import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { publicApiClient } from '@/lib/api/client';
import { useLoadingContext, useAuth } from '@/hooks';
import { MobileBookingCategoryDetails } from '@/features/booking/components/mobile/MobileBookingCategoryDetails';
import type { EventBookingCategoryData } from '@/features/booking/types/ticketBookingTypes';

const EventBookingCategoryDetailsPage: React.FC = () => {
  const { eventBookingCategoryId } = useParams<{ eventBookingCategoryId: string }>();
  const navigate = useNavigate();
  const { startLoading, stopLoading } = useLoadingContext();
  const { isAuthenticated } = useAuth();

  const [eventBookingCategory, setEventBookingCategory] = useState<EventBookingCategoryData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchEventBookingCategory = async () => {
      if (!eventBookingCategoryId) return;
      
      try {
        startLoading('event-booking-category-details');
        setIsLoading(true);
        setError(null);

        const response: any = await publicApiClient.get(`/api/public/v1/event-booking-category/${eventBookingCategoryId}`);
        
        if (response?.data?.event_booking_category) {
          // Map to match the expected unified EventBookingCategoryData format minimally needed here
          const ebc = response.data.event_booking_category;
          
          setEventBookingCategory({
            ...ebc,
            booking_category: {
               ...ebc.booking_category,
               // Extract media from the response if available
               media: ebc.booking_category.media || [],
            }
          });
        } else {
          throw new Error('Invalid response data');
        }
      } catch (err) {
        console.error('Error fetching booking category details:', err);
        setError('Failed to load category details. Please try again.');
      } finally {
        setIsLoading(false);
        stopLoading('event-booking-category-details');
      }
    };

    fetchEventBookingCategory();
    
    return () => {
      stopLoading('event-booking-category-details');
    };
  }, [eventBookingCategoryId, startLoading, stopLoading]);

  // While fetching initially, wait for global loader; do not mount error state.
  if (isLoading) {
    return null;
  }

  // Handle error states
  if (error || !eventBookingCategory) {
    return (
      <div className="flex flex-col h-screen items-center justify-center bg-[#050505] px-4 text-center">
        <h2 className="text-xl font-bold text-white mb-2">Oops!</h2>
        <p className="text-gray-400 mb-6">{error || 'Category not found'}</p>
        <button
          onClick={() => navigate(-1)}
          className="px-6 py-2 bg-white/10 text-white rounded-full font-medium hover:bg-white/20 transition-colors"
        >
          Go Back
        </button>
      </div>
    );
  }

  return (
    <MobileBookingCategoryDetails 
      eventBookingCategory={eventBookingCategory} 
      isAuthenticated={isAuthenticated} 
    />
  );
};

export default EventBookingCategoryDetailsPage;
